/**
 * External Dependencies
 */
import assign from 'lodash.assign';

/**
 * WordPress Dependencies
 */
const { createHigherOrderComponent } = wp.compose;
const { Fragment } = wp.element;
const { InspectorControls } = wp.blockEditor;
const { PanelBody, PanelRow, ToggleControl } = wp.components;
const { addFilter } = wp.hooks;
const { __ } = wp.i18n;
const { dispatch } = wp.data;

// Blocks that do not support custom attributes
const globallyRestricted = [
	'core/freeform',
	'core/legacy-widget',
	'core/widget-area'
];

// Allowed breakpoints (must match PHP)
const ALLOWED_BREAKPOINTS = ['mobile', 'tablet', 'desktop', 'wide'];

/**
 * Add attributes to supported blocks.
 */
const addAttributes = (settings) => {
	if (globallyRestricted.includes(settings.name)) {
		return settings;
	}

	const attributes = {
		responsiveBlockControl: {
			type: 'object',
			default: {
				mobile: false,
				tablet: false,
				desktop: false,
				wide: false
			}
		}
	};

	settings.attributes = assign(settings.attributes, attributes);
	return settings;
};

addFilter(
	'blocks.registerBlockType',
	'responsiveBlockControl/attributes',
	addAttributes
);

/**
 * Inspector controls HOC.
 */
const withVisibilityControl = createHigherOrderComponent((BlockEdit) => {
	return (props) => {
		const {
			clientId,
			attributes,
			isSelected
		} = props;

		const responsiveBlockControl =
			typeof attributes.responsiveBlockControl === 'object'
				? attributes.responsiveBlockControl
				: {};

		const handleChange = (device) => {
			if (!ALLOWED_BREAKPOINTS.includes(device)) {
				return;
			}

			const newState = {
				...responsiveBlockControl,
				[device]: !Boolean(responsiveBlockControl[device])
			};

			dispatch('core/block-editor').updateBlockAttributes(
				clientId,
				{ responsiveBlockControl: newState }
			);
		};

		return (
			<Fragment>
				<BlockEdit {...props} />
				{isSelected && props.name !== 'core/freeform' && (
					<InspectorControls>
						<PanelBody
							title={__('Visibility', 'responsive-block-control')}
							initialOpen={false}
						>
							{ALLOWED_BREAKPOINTS.map((device) => (
								<PanelRow key={device}>
									<ToggleControl
										label={__(
											`Hide on ${device.charAt(0).toUpperCase() + device.slice(1)}`,
											'responsive-block-control'
										)}
										checked={!!responsiveBlockControl[device]}
										onChange={() => handleChange(device)}
									/>
								</PanelRow>
							))}
						</PanelBody>
					</InspectorControls>
				)}
			</Fragment>
		);
	};
}, 'withVisibilityControl');

addFilter(
	'editor.BlockEdit',
	'responsiveBlockControl/control',
	withVisibilityControl
);

/**
 * Add safe data attribute in editor only.
 */
const addWrapperDataAttribute = createHigherOrderComponent(
	(BlockListBlock) => {
		return (props) => {
			const { responsiveBlockControl } = props.attributes || {};
			let wrapperProps = props.wrapperProps || {};

			if (typeof responsiveBlockControl === 'object') {
				const hiddenOn = ALLOWED_BREAKPOINTS.filter(
					(bp) => responsiveBlockControl[bp] === true
				);

				if (hiddenOn.length > 0) {
					wrapperProps = assign(wrapperProps, {
						'data-is-hidden-on': `${__('Hidden on', 'responsive-block-control')} ${hiddenOn.join(
							' '
						)}`
					});
				}
			}

			return <BlockListBlock {...props} wrapperProps={wrapperProps}/>;
		};
	},
	'addWrapperDataAttribute'
);

addFilter(
	'editor.BlockListBlock',
	'responsiveBlockControl/addWrapperDataAttribute',
	addWrapperDataAttribute
);
